import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useCart } from './CartContext';
import './Cart.css';
import TotalSummary from './TotalSummary';
import { type } from '@testing-library/user-event/dist/type';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Cart = () => {
    const { cartItems, removeFromCart, setCartItems, quantities, updateQuantity } = useCart();
    const [selectedItems, setSelectedItems] = useState([]);
    // const [quantities, setQuantities] = useState({});
    const [isSelectAll, setIsSelectAll] = useState(false);
    const [focusedItem, setFocusedItem] = useState(null);
    const [customerName, setCustomerName] = useState('');
    const [memo, setMemo] = useState('');
    const [couponCode, setCouponCode] = useState(''); //쿠폰코드상태
    const [coupon, setCoupon] = useState(null); //쿠폰정보상태
    const history = useHistory();

    const urlParams = new URLSearchParams(window.location.search);
    const name = urlParams.get('name');
    const session = urlParams.get('session');

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const name = urlParams.get('name');
        const session = urlParams.get('session');
        
        if (name) {
            setCustomerName(name);
        }
    },[cartItems]);


    const handleSelectItem = (id) => {
        // console.log(id)
        setSelectedItems((prevSelected) => 
            prevSelected.includes(id)
                ? prevSelected.filter(itemId => itemId !== id)
                : [...prevSelected, id]
        );
    };

    const handleSelectAll = () => {
        if (selectedItems.length === cartItems.length) {
            setSelectedItems([]);
            setIsSelectAll(false);  // 전체 선택 해제
        } else {
            setSelectedItems(cartItems.map(item => item.id));
            setIsSelectAll(true);  // 전체 선택
        }
    };

    const handleRemoveSelected = () => {
        setCartItems(cartItems.filter(item => !selectedItems.includes(item.id)));
        setSelectedItems([]);
        setIsSelectAll(false);  // 선택된 항목 제거 시 전체 선택 해제
    };


    const handleQuantityChange = (id, value) => {
        if (value >= 1 && value <= 999) {
            updateQuantity(id, value);
        }
    };

    const increaseQuantity = (id) => {
        updateQuantity(id, Math.min(999, quantities[id] + 1));
    };

    const decreaseQuantity = (id) => {
        updateQuantity(id, Math.max(1, quantities[id] - 1));
    };

    const handleBackToShopping = () => {
        history.push(`/?session=${session}&name=${name}`);
    };

    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const handleFocusItem = (id) => {
        setFocusedItem(null);
    }

    const handleMemoChange = (e) => {
        setMemo(e.target.value);
    }

    const handleCouponCodeChange = (e) => {
        setCouponCode(e.target.value);
    }

    const applyCoupon = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/cart/coupon/get?couponCode=${couponCode}&name=${name}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
            });
            
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            
            const data = await response.json();
            // console.log(data);
    
            if (data.type && data.discountValue >= 0) {
                setCoupon(data);
            } else {
                alert('Invalid coupon code');
                setCoupon(null);
            }
        } catch (error) {
            alert('Invalid coupon code');
            setCoupon(null);
        }
    };

    // 서버로 전송
    const handleCompleteSelection = async () => {
        if (cartItems.length === 0) return; // 장바구니가 비어있으면 종료

        history.push('/completed'); // 완료페이지로 이동

        // const customerName = "KSD";
        const date = formatDate(new Date());

        const productList = cartItems.map(item => ({
            id: item.id,
            displayPrice: parseFloat(item.price.replace(/,/g, '')), // 쉼표 제거 후 숫자로 변환
            amount: quantities[item.id]
        }));

        const totalPrice = productList.reduce((acc, product) => acc + product.displayPrice * product.amount, 0);
        let finalPrice = totalPrice;
        if (coupon) {
            if (coupon.type === 'PERCENTAGE_DISCOUNT') {
                finalPrice = totalPrice * (1 - coupon.discountValue / 100);
                
            } else if (coupon.type === 'FIXED_AMOUNT_DISCOUNT') {
                finalPrice = totalPrice - coupon.discountValue;
            }
        }

        const data = {
            customerName,
            date,
            finalPrice,
            productList,
            memo,
            coupon: coupon ? {
                type: coupon.type,
                discountValue: coupon.discountValue
            } : null
        };
        // console.log(data)

        try {
            const response = await fetch(`${API_BASE_URL}/api/cart/customer/save`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            });

            if (response.ok) {
                // 성공적으로 데이터를 전송하면
                // console.log('Data sent successfully');
                // history.push('/completed');
            } else {
                console.error('Failed to send data');
            }
        } catch (error) {
            console.error('Error sending data:', error);
        }
    };

    // console.log('cartItems:', cartItems);

    // 카테고리 이름 매핑
    const reverseCategoryMap = {
        "봉지스낵": "봉지 스낵",
        "갑스낵": "갑 스낵",
        "쿠키파이": "쿠키/파이",
        "젤리초콜릿": "젤리/초콜릿",
        "견과류에너지바": "견과류/에너지바",
        "음료": "음료",
        "우유두유": "우유/두유",
        "차티백커피": "차/티백/커피",
        "시리얼컵라면": "시리얼/컵라면"
    };

    // 장바구니 아이템을 카테고리별로 그룹화
    const groupItemsByCategory = (items) => {
        return items.reduce((acc, item) => {
            const category = reverseCategoryMap[item.category] || '기타';
            if (!acc[category]) {
                acc[category] = [];
            }
            acc[category].push(item);
            return acc;
        }, {});
    };

    const groupedItems = groupItemsByCategory(cartItems);
    const totalItems = Object.values(groupedItems).reduce((acc, items) => acc + items.length, 0);

    return (
        <div className="cart-page">
            <header className="header">
                <div className='top-section'>
                    <img src="/logo.png" alt="Cleanier Snack" className="logo" />
                </div>
                <div className='info-section'>
                    <div className='info-text'>
                        <img src="/building.png" alt="Cleanier Snack" className="info-logo" />
                        <div>
                            <p className="info-title">{customerName || "고객 공간명이 들어갑니다"}</p>
                            <p>안녕하세요. 클리니어 스낵 매대에 채워질 상품을 골라주세요!</p>
                        </div>
                    </div>
                    {/* <button className='create-pdf-button'>
                        <img src="/download.png" alt='download' className='download-image'/>
                        PDF 다운로드
                    </button> */}
                </div>
            </header>
            <div className='real-container'>
            <div className='cart-summary-container'>
                <div className='cart-container'>
                    <div className="cart-actions">
                        <div className='select-all-container'>
                            <button 
                                className={`select-all-button ${isSelectAll ? 'selected' : ''}`}
                                onClick={handleSelectAll}>     
                            </button>
                            <span className='select-all-label'>전체선택</span>
                        </div>
                        <button className='remove-selected-button' onClick={handleRemoveSelected}>
                            선택상품 삭제
                        </button>
                    </div>
                    {cartItems.length === 0 ? (
                        <div className='empty-cart'>
                            <p>장바구니에 상품이 없습니다.</p>
                        </div>
                    ) : (
                        Object.entries(groupedItems).map(([category, items]) => (
                            <div key={category} className="cart-list">
                                <h3>{category} {items.length}종</h3>
                                {items.map((item) => (
                                    <div className="cart-row" key={item.id}>
                                        <button className={`check-button ${selectedItems.includes(item.id) ? 'selected' : ''}`}
                                            onClick={() => handleSelectItem(item.id)}> </button>
                                        <img src={item.image} alt={item.name} className="cart-item-image" />
                                        <div className="cart-item-details">
                                            <div className="title">{item.name}</div>
                                            <div className="item-price">{item.price}원</div>
                                        </div>
                                        <div className={`cart-item-quantity ${focusedItem === item.id ? 'focused' : ''}`}>
                                            <button onClick={() => decreaseQuantity(item.id)}
                                                onFocus={() => handleFocusItem(item.id)}
                                                // onBlur={handleBlurItem}
                                            >–</button>
                                            <input 
                                                type="number"
                                                value={quantities[item.id]}
                                                onChange={(e) => handleQuantityChange(item.id, parseInt(e.target.value))}
                                                min="1"
                                                max="999"
                                                onFocus={() => handleFocusItem(item.id)}
                                                // onBlur={handleBlurItem}
                                            />
                                            <button onClick={() => increaseQuantity(item.id)}
                                                onFocus={() => handleFocusItem(item.id)}
                                                // onBlur={handleBlurItem}
                                            >+</button>
                                        </div>
                                        <button className="cart-item-remove" onClick={() => removeFromCart(item.id)}>
                                            <img src='/trash.png' alt='remove'/>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ))
                    )}
                </div>
                <TotalSummary
                    cartItems={cartItems}
                    quantities={quantities}
                    onCompleteSelection={handleCompleteSelection}
                    handleBackToShopping={handleBackToShopping} 
                    reverseCategoryMap={reverseCategoryMap}
                    session={session}
                    name={name}
                    memo={memo}
                    handleMemoChange={handleMemoChange}
                    couponCode={couponCode}
                    handleCouponCodeChange={handleCouponCodeChange}
                    applyCoupon={applyCoupon}
                    coupon={coupon}
                    setCoupon={setCoupon}
                    setCouponCode={setCouponCode}
                    totalItems={totalItems}/>
            </div>
        </div>
        </div>

    );
};

export default Cart;